import { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Safe wrapper - localStorage moze byc niedostepny (private browsing, privacy plugin,
// strict storage policy w niektorych przegladarkach). useState() initializer rzucal
// "Cannot read properties of null" - error_logs 22/06.
const safeLS = {
    get(key) {
        try { return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null; }
        catch { return null; }
    },
    set(key, value) {
        try { localStorage.setItem(key, value); } catch { /* ignore */ }
    },
    remove(key) {
        try { localStorage.removeItem(key); } catch { /* ignore */ }
    },
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(() => safeLS.get('token'));
    const [isLoading, setIsLoading] = useState(true);

    // Load user on mount if token exists
    useEffect(() => {
        const loadUser = async () => {
            if (token) {
                try {
                    const response = await fetch(`${API_URL}/api/auth/me`, {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    });
                    
                    if (response.ok) {
                        const userData = await response.json();
                        setUser(userData);
                    } else {
                        // Token invalid, clear it
                        safeLS.remove('token');
                        setToken(null);
                    }
                } catch (error) {
                    console.error('Failed to load user:', error);
                    safeLS.remove('token');
                    setToken(null);
                }
            }
            setIsLoading(false);
        };

        loadUser();
    }, [token]);

    const register = async (data) => {
        const response = await fetch(`${API_URL}/api/auth/register`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });

        const result = await response.json();

        if (!response.ok) {
            throw new Error(result.detail || 'Błąd rejestracji');
        }

        // Save token and user
        safeLS.set('token', result.access_token);
        setToken(result.access_token);
        setUser(result.user);

        return result;
    };

    const login = async (identifier, password) => {
        const response = await fetch(`${API_URL}/api/auth/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ identifier, password })
        });

        const result = await response.json();

        if (!response.ok) {
            throw new Error(result.detail || 'Nieprawidłowy login lub hasło');
        }

        // Save token and user
        safeLS.set('token', result.access_token);
        setToken(result.access_token);
        setUser(result.user);

        return result;
    };

    const logout = async () => {
        try {
            if (token) {
                await fetch(`${API_URL}/api/auth/logout`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
            }
        } catch (error) {
            console.error('Logout error:', error);
        }

        safeLS.remove('token');
        setToken(null);
        setUser(null);
    };

    const updateProfile = async (data) => {
        const response = await fetch(`${API_URL}/api/auth/me`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(data)
        });

        const result = await response.json();

        if (!response.ok) {
            throw new Error(result.detail || 'Błąd aktualizacji profilu');
        }

        setUser(result);
        return result;
    };

    const checkAvailability = async (nickname = null, email = null, phone = null) => {
        const params = new URLSearchParams();
        if (nickname) params.append('nickname', nickname);
        if (email) params.append('email', email);
        if (phone) params.append('phone', phone);

        const response = await fetch(`${API_URL}/api/auth/check-availability?${params}`);
        return await response.json();
    };

    const value = {
        user,
        token,
        isLoading,
        isAuthenticated: !!user,
        register,
        login,
        logout,
        updateProfile,
        checkAvailability
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};
